import { InsuranceView, useToast } from '@elva/ui';
import { useLocationStore, useWorkspaceLabel } from '@elva/platform';
import { useMemo } from 'react';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { adaptPracticeCarriers, adaptPracticeProviders } from '../adapters/settingsAdapters';
import { useCarriers, useDeleteCarrier } from '../hooks/useCarriers';
import { useProviders } from '../hooks/useProviders';
import { useUpdateProviderNetwork } from '../hooks/useProviderNetwork';

/** Practice management › Insurance & network (spec 15 §7), on the carriers + provider-network APIs. */
export function SettingsInsuranceNetworkPage() {
  const workspaceLabel = useWorkspaceLabel();
  const locationId = useLocationStore((s) => s.selectedCoreLocationId);
  const { data: carriersData, isLoading: carriersLoading, isError } = useCarriers(true);
  const { data: providersData, isLoading: providersLoading } = useProviders(true);
  const deleteCarrier = useDeleteCarrier();
  const updateNetwork = useUpdateProviderNetwork();
  const { toast } = useToast();

  const carriers = useMemo(
    () => adaptPracticeCarriers(carriersData?.results ?? []),
    [carriersData?.results],
  );
  const providers = useMemo(
    () => adaptPracticeProviders(providersData?.providers ?? []),
    [providersData?.providers],
  );
  const ready = Boolean(carriersData && providersData);

  return (
    <ElvaSectionState isLoading={carriersLoading || providersLoading || !ready} isError={isError}>
      {ready ? (
        <InsuranceView
          key={locationId ?? 'none'}
          carriers={carriers}
          providers={providers}
          location={workspaceLabel}
          onRemoveCarrier={async (carrier) => {
            const id = Number(carrier.id);
            if (Number.isNaN(id)) return;
            try {
              await deleteCarrier.mutateAsync(id);
            } catch {
              toast({ title: `Could not remove ${carrier.name}`, variant: 'error' });
            }
          }}
          onSaveNetwork={async (providerId, carrierIds) => {
            try {
              await updateNetwork.mutateAsync({
                providerId,
                data: { carrier_ids: carrierIds.map(Number).filter((n) => !Number.isNaN(n)) },
              });
            } catch {
              toast({ title: 'Could not save the network', variant: 'error' });
            }
          }}
        />
      ) : null}
    </ElvaSectionState>
  );
}
